import type { Dayjs } from 'dayjs';
import AbstractApiModel from '@/models/abstracts/AbstractApiModel';
import doc from '@model-core/decorators/doc';
import { EXPORT_STATUS } from '@/models/references/references';
import { referenceOf } from '@/stores/useReferenceOptions';
import { urnOf } from '@/models/urn';

/** URN de l'export : source de son fichier (filtre typeSource.source.entityUrn des fichiers) */
export const exportUrn = (exportId: id | number) => urnOf('export', exportId);

/** Filtres de GET /time_sheets enregistrés sur l'export (ceux de la liste de l'Historique) */
export type ExportFilters = Record<string, string | number | (string | number)[] | Record<string, string>>;

/**
 * Export des feuilles d'heures d'une société (GET / POST /exports, Dirigeant) : un type (CSV sans ou avec détails, ZIP
 * des PDF), les filtres de la liste exportée, un statut (En attente, En cours, Terminé, Échoué) ; son fichier, généré
 * en asynchrone par l'API, se télécharge une fois Terminé (downloadExport). Type et statut arrivent avec leur id.
 * Filtres : company, exportType, exportStatus ; tri order[createdAt].
 */
export default class ExportModel extends AbstractApiModel {
  protected _path = '/exports/{?id}';

  declare _tsFilters: { company?: string; exportType?: id; exportStatus?: id };
  declare _tsSorts: { createdAt?: unknown };

  @doc.date declare createdAt: Dayjs;
  @doc.string declare createdBy: string;

  get companyId(): number {
    return Number(this.get<string, string>('company.@id', '').split('/').pop()) || 0;
  }

  get filters(): ExportFilters {
    return this.get<ExportFilters, ExportFilters>('filters', {}) ?? {};
  }

  get exportTypeReference(): string {
    return referenceOf('exportTypes', this.get<unknown, unknown>('exportType', null))?.reference ?? '';
  }

  get exportTypeLabel(): string {
    return referenceOf('exportTypes', this.get<unknown, unknown>('exportType', null))?.label ?? '';
  }

  get exportStatusReference(): string {
    return referenceOf('exportStatuses', this.get<unknown, unknown>('exportStatus', null))?.reference ?? '';
  }

  get exportStatusLabel(): string {
    return referenceOf('exportStatuses', this.get<unknown, unknown>('exportStatus', null))?.label ?? '';
  }

  /** En attente ou en cours : le statut est suivi jusqu'à Terminé ou Échoué */
  get isRunning(): boolean {
    return [EXPORT_STATUS.PENDING, EXPORT_STATUS.PROCESSING].includes(this.exportStatusReference as never);
  }

  get isCompleted(): boolean {
    return this.exportStatusReference === EXPORT_STATUS.COMPLETED;
  }

  get isFailed(): boolean {
    return this.exportStatusReference === EXPORT_STATUS.FAILED;
  }
}
